import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faFileAlt,
    faHistory,
    faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import './RFCExpandableButton.css';

/**
 * RFCExpandableButton Component
 * 
 * Smart expandable button for RFC actions.
 * Shows single action if only one is available, or expands to show both.
 * 
 * Props:
 * - pendingCount: number - Count of pending RFCs
 * - historyCount: number - Count of reviewed RFCs
 * - onPendingClick: function - Callback when "Pending RFCs" is clicked
 * - onHistoryClick: function - Callback when "History" is clicked
 */
const RFCExpandableButton = ({ 
    pendingCount = 0, 
    historyCount = 0, 
    onPendingClick, 
    onHistoryClick 
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const buttonRef = useRef(null);
    
    const hasPending = pendingCount > 0;
    const hasHistory = historyCount > 0;
    const hasBoth = hasPending && hasHistory;
    
    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (buttonRef.current && !buttonRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };
        
        if (isExpanded) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isExpanded]);
    
    // If neither pending nor history, don't render
    if (!hasPending && !hasHistory) {
        return null;
    }
    
    // If only one action is available, render simple button
    if (!hasBoth) {
        if (hasPending) {
            return (
                <button
                    className="rfc-expandable-button rfc-single-action rfc-pending-btn"
                    onClick={onPendingClick}
                    type="button"
                >
                    <FontAwesomeIcon icon={faFileAlt} className="rfc-expandable-icon" />
                    <span>RFC Details</span>
                    {pendingCount > 0 && (
                        <span className="rfc-badge rfc-badge-pending">{pendingCount}</span>
                    )}
                </button>
            );
        }
        
        if (hasHistory) {
            return (
                <button
                    className="rfc-expandable-button rfc-single-action rfc-history-btn"
                    onClick={onHistoryClick}
                    type="button"
                >
                    <FontAwesomeIcon icon={faHistory} className="rfc-expandable-icon" />
                    <span>RFC History</span>
                    {historyCount > 0 && (
                        <span className="rfc-badge rfc-badge-history">{historyCount}</span>
                    )}
                </button>
            );
        }
    }
    
    // If both actions are available, render expandable menu
    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };
    
    return (
        <div className="rfc-expandable-container" ref={buttonRef}>
            {/* Expanded Options */}
            {isExpanded && (
                <div className="rfc-expandable-menu">
                    <button
                        className="rfc-menu-option rfc-menu-pending"
                        onClick={() => {
                            setIsExpanded(false);
                            onPendingClick();
                        }}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faFileAlt} className="rfc-menu-icon" />
                        <span className="rfc-menu-text">
                            Pending RFCs
                            {pendingCount > 0 && (
                                <span className="rfc-menu-count">({pendingCount})</span>
                            )}
                        </span>
                    </button>
                    
                    <button
                        className="rfc-menu-option rfc-menu-history"
                        onClick={() => {
                            setIsExpanded(false);
                            onHistoryClick();
                        }}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faHistory} className="rfc-menu-icon" />
                        <span className="rfc-menu-text">
                            RFC History
                            {historyCount > 0 && (
                                <span className="rfc-menu-count">({historyCount})</span>
                            )}
                        </span>
                    </button>
                </div>
            )}
            
            {/* Main Toggle Button */}
            <button
                className={`rfc-expandable-button rfc-multi-action ${isExpanded ? 'expanded' : ''}`}
                onClick={toggleExpanded}
                type="button"
            >
                <FontAwesomeIcon 
                    icon={faExclamationCircle} 
                    className={`rfc-expandable-icon ${hasPending ? 'pulsing' : ''}`} 
                />
                <span>RFCs</span>
                {(pendingCount + historyCount) > 0 && (
                    <span className="rfc-badge rfc-badge-total">
                        {pendingCount + historyCount}
                    </span>
                )}
            </button>
        </div>
    );
};

export default RFCExpandableButton;

